import {Chip} from '@mui/material'
import {Box, Checkbox} from '@viptech/react-components'
import moment from 'moment'
import {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import CheckboxTable from 'src/components/CheckboxTable'
import LinkWithAction from 'src/components/LinkWithAction'
import RowHover from 'src/components/RowHover/RowHover'
import {DataTablePagination} from 'src/components/data-table-pagination'
import Table from 'src/components/table/Table'
import TableData from 'src/components/table/components/tableData/TableData'
import {TableHeaderColumn, TableHeaderRow} from 'src/components/table/components/tableHeader/TableHeaderBase'
import {Company} from 'src/services/api'
import {Page} from 'src/types'
import {RenderIfConditionIsMet} from 'src/utilities/RenderIfConditionIsMet'
import createStyleSheet from 'src/utilities/createStyleSheet'
import CompanyMenuOptions from './CompanyButtonOptions'

type CompanyGridTableProps = {
  companies: Company[]
  loadingCompanies: boolean
  totalElements: number
  companiesPage: Page
  setCompaniesPage: Function
  openEditModal: (company: Company) => void
  selectedCompaniesIds: number[]
  setSelectedCompaniesIds: (ids: number[]) => void
  openConfirmCredentialsDialog: (company: Company) => void
  deleteCompany: (companyId?: number) => void
}

type CompanyTableProps = {
  id: number
  name: string
  document: string
  apiUser?: string
  retail: boolean
  dateFormated: string
  expirationDate?: string
  contract: string
  isPoc: boolean
  self: Company
}

const columns = [
  {
    id: 'checkbox',
    name: '',
  },
  {
    id: 'id',
    name: 'ID',
  },
  {
    id: 'name',
    name: 'Nome',
  },
  {
    id: 'document',
    name: 'CPF/CNPJ',
  },
  {
    id: 'retail',
    name: 'Tipo',
  },
  {
    id: 'apiUser',
    name: 'Usuário',
  },
  {
    id: 'expirationDate',
    name: 'Data de Expiração',
  },
  {
    id: 'actions',
    name: '',
  },
]

const columnsNoPermission = [
  {
    id: 'checkbox',
    name: '',
  },
  {
    id: 'id',
    name: 'ID',
  },
  {
    id: 'name',
    name: 'Nome da Empresa',
  },
  {
    id: 'document',
    name: 'CPF/CNPJ',
  },
  {
    id: 'expirationDate',
    name: 'Data de Expiração',
  },
]

function CompanyGridTable(props: CompanyGridTableProps) {
  const {
    companies: companiesRaw,
    loadingCompanies,
    totalElements,
    companiesPage,
    setCompaniesPage,
    openEditModal,
    selectedCompaniesIds,
    setSelectedCompaniesIds,
    openConfirmCredentialsDialog,
    deleteCompany,
  } = props
  const hasUpdateCompanyPermission = ValidateUserPermissions('companies:update')
  const hasDeleteCompanyPermission = ValidateUserPermissions('companies:delete')
  const companiesPageChange = (page: number) => setCompaniesPage((lastPage: Page) => ({...lastPage, page}))
  const companiesPageSizeChange = (pageSize: number) => setCompaniesPage((lastPage: Page) => ({...lastPage, pageSize}))
  const [companies, setCompanies] = useState<CompanyTableProps[]>([])
  const navigate = useNavigate()

  const tableControl = {
    pageIndex: companiesPage.page,
    pageSize: companiesPage.pageSize,
    total: totalElements,
    setPageSize: companiesPageSizeChange,
    setPageIndex: companiesPageChange,
    nextPage: () => {
      companiesPageChange(companiesPage.page + 1)
    },
    previousPage: () => {
      companiesPageChange(companiesPage.page - 1)
    },
  }

  useEffect(() => {
    const companiesMapped = companiesRaw.map((it) => {
      const days = moment().format('YYYY-MM-DD')
      const inExpiration = it.expirationDate
      const timeDiff = moment(inExpiration).diff(days, 'day')
      const isRetail = it.permissions?.map((it) => it.codename).includes('analytic-cameras:read')
      const isPoc = it.isPoc ?? false

      return {
        id: it.id,
        name: it.name,
        document: it.document,
        apiUser: it.apiUser,
        retail: !!isRetail,
        dateFormated: it.expirationDate ? moment(it.expirationDate).calendar() : '-',
        expirationDate: it.expirationDate,
        contract: it.expirationDate ? (timeDiff < 0 ? 'Expirado' : '-') : '-',
        isPoc: isPoc,
        self: it,
      }
    })
    setCompanies(companiesMapped)
  }, [companiesRaw])

  const onSelectAll = () => {
    if (selectedCompaniesIds.length) return setSelectedCompaniesIds([])
    const allIds = companies.map((row) => row.id)
    setSelectedCompaniesIds(allIds)
  }

  const customHeader = () => {
    const columnsToRender = hasUpdateCompanyPermission ? columns : columnsNoPermission
    return (
      <thead>
        <TableHeaderRow>
          {columnsToRender.map((col) =>
            col.id === 'checkbox' ? (
              <TableHeaderColumn>
                <div style={{paddingLeft: '10px'}}>
                  <Checkbox
                    id='check'
                    checked={selectedCompaniesIds.length ? companies.length === selectedCompaniesIds.length : false}
                    onChange={onSelectAll}
                  />
                </div>
              </TableHeaderColumn>
            ) : (
              <TableHeaderColumn>{col.name}</TableHeaderColumn>
            ),
          )}
        </TableHeaderRow>
      </thead>
    )
  }

  if (!hasDeleteCompanyPermission) {
    columns.shift()
    columnsNoPermission.shift()
  }

  function renderNameWithChip(row: CompanyTableProps) {
    return (
      <LinkWithAction
        onClick={() => {
          if (hasUpdateCompanyPermission) openEditModal(row.self)
        }}>
        <Box display='flex' columnGap='15px'>
          {row.name}

          {row.contract === 'Expirado' ? (
            <Chip label={row.contract} color='error' size='small' variant='outlined' />
          ) : null}
          {row.isPoc ? <Chip label='POC' color='info' size='small' variant='outlined' /> : null}
        </Box>
      </LinkWithAction>
    )
  }

  const conditionalRenderTableData = (row: CompanyTableProps) => {
    if (hasUpdateCompanyPermission)
      return (
        <>
          <TableData>{row.document}</TableData>
          <TableData>{row.retail ? 'Varejo' : 'Padrão'}</TableData>
          <TableData>{row.apiUser}</TableData>
          <TableData>{row.dateFormated}</TableData>
          <TableData>
            <CompanyMenuOptions
              oneObject={row.id}
              onClick={(event) => {
                if (event === 'set-permissions') navigate(row.id.toString())
                if (event === 'edit-company') openEditModal(row.self)
                if (event === 'update-credentials') openConfirmCredentialsDialog(row.self)
                if (event === 'delete') {
                  setSelectedCompaniesIds([+row.id])
                  deleteCompany(row.id)
                }
              }}
              hasDeleteCompanyPermission={hasDeleteCompanyPermission}
            />
          </TableData>
        </>
      )
    return (
      <>
        <TableData>{row.document}</TableData>
        <TableData>{row.dateFormated}</TableData>
      </>
    )
  }

  return (
    <Box style={styles.gridItem}>
      <Box flex={1} display={'flex'}>
        <Table
          columns={hasUpdateCompanyPermission ? columns : columnsNoPermission}
          skeletonRowsAmount={10}
          isLoading={loadingCompanies}
          tableHeaderChildren={customHeader()}>
          {companies.map((row) => (
            <RowHover>
              <RenderIfConditionIsMet condition={hasDeleteCompanyPermission}>
                <TableData paddingLeft='10px'>
                  <CheckboxTable id={row.id} selectedIds={selectedCompaniesIds} setSelectedIds={setSelectedCompaniesIds} />
                </TableData>
              </RenderIfConditionIsMet>
              <TableData>{row.id}</TableData>
              <TableData>{renderNameWithChip(row)}</TableData>
              {conditionalRenderTableData(row)}
            </RowHover>
          ))}
        </Table>
      </Box>
      {!loadingCompanies && companies.length === 0 ? (
        <Box style={styles.noRegisters}>Nenhum registro encontrado</Box>
      ) : (
        <Box style={styles.pagination}>
          <DataTablePagination table={tableControl} />
        </Box>
      )}
    </Box>
  )
}

const styles = createStyleSheet({
  gridItem: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    padding: '0px 20px',
    height: '100%',
    minHeight: '79vh',
  },
  noRegisters: {
    fontFamily: "'Inter', 'sans-serif'",
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 450,
    minHeight: '65vh',
    '@media(max-width:1540px)': {
      fontSize: '0.95em',
    },
  },
  pagination: {
    display: 'flex',
    margin: '15px 0',
  },
})

export default CompanyGridTable
